import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, IconButton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback, useState } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { InflationFormValues } from '../InflationPage.hooks';

const INITIAL_VALUES: InflationFormValues = {
  currentMonthlyExpense: 3_000_000,
  inflationRate: 3,
  yearsToRetirement: 25,
  postRetirementYears: 30,
};

const schema = yup.object().shape({
  currentMonthlyExpense: yup.number().min(100_000, '월 생활비는 10만 원 이상이어야 합니다.').max(10_000_000, '1,000만 원 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  inflationRate: yup.number().min(0, '0% 이상이어야 합니다.').max(15, '15% 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  yearsToRetirement: yup.number().min(1, '1년 이상이어야 합니다.').max(50, '50년 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  postRetirementYears: yup.number().min(5, '5년 이상으로 설정해 주세요.').max(50, '50년 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
});

function FieldTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);

  return (
    <Tooltip open={open} onClose={() => setOpen(false)} title={text} arrow placement="top">
      <IconButton size="small" onClick={() => setOpen((v) => !v)} sx={{ p: 0.25, color: '#9ca3af' }}>
        <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Tooltip>
  );
}

export interface InflationFormProps {
  onSubmit: (values: InflationFormValues) => void;
  className?: string;
}

export function InflationForm({ onSubmit, className }: InflationFormProps) {
  const handleSubmit = useCallback((values: InflationFormValues) => onSubmit(values), [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="currentMonthlyExpense">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>현재 월 생활비 (원)<FieldTooltip text="지금 한 달에 쓰는 생활비입니다. 은퇴 후에도 이 수준을 유지하고 싶다는 기준점으로 사용합니다." /></span>}
                  placeholder="3,000,000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.currentMonthlyExpense && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentMonthlyExpense}</div>}

            <Field name="inflationRate">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>연 물가 상승률 (%)<FieldTooltip text="한국의 최근 10년 평균 소비자물가 상승률은 약 2~2.5%입니다. 보수적으로 계획하려면 3%를 사용하세요." /></span>}
                  placeholder="3"
                  required
                  name={field.name}
                  value={field.value}
                  decimalScale={1}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.inflationRate && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.inflationRate}</div>}

            <Field name="yearsToRetirement">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>은퇴까지 남은 기간 (년)<FieldTooltip text="이 기간 동안 물가가 계속 오릅니다. 기간이 길수록 생활비 격차가 커집니다." /></span>}
                  placeholder="25"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.yearsToRetirement && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.yearsToRetirement}</div>}

            <Field name="postRetirementYears">
              {({ field }: FieldProps) => (
                <NumericInput
                  label={<span style={{ display: 'flex', alignItems: 'center' }}>은퇴 후 생활 기간 (년)<FieldTooltip text="기대여명을 기준으로 설정하세요. 안전 마진을 위해 5년 더하는 것을 권장합니다." /></span>}
                  placeholder="30"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.postRetirementYears && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.postRetirementYears}</div>}
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large" sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              구매력 계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
